import { TASK_LIVE_FORBIDDEN, type TaskLiveMessage } from '@shared/types/tasks';
import { useAuthStore } from '@/stores/authStore';
import { whenSessionReady } from '@/lib/session-ready';
import { getTaskNotifications } from '@/lib/tasks-api';

const PING_MS = 30_000;
const MAX_BACKOFF_MS = 30_000;

function liveUrl(token: string, projectId: string): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/tasks/live?token=${encodeURIComponent(token)}&project=${encodeURIComponent(projectId)}`;
}

/**
 * One socket to a project's live hub, for a page that follows several projects at once (the master
 * board, T-26; the Inbox, T-37). `onMessage` gets each message, and null after a reconnect (it may
 * have missed some). Like TaskLiveConnector: a hidden tab doesn't reconnect until it's shown, and a
 * refused socket isn't retried. Answers the function that closes it.
 */
export function openProjectLive(projectId: string, onMessage: (message: TaskLiveMessage | null) => void): () => void {
  return openLiveSocket<TaskLiveMessage>((token) => liveUrl(token, projectId), onMessage);
}

/**
 * A live socket the server pushes JSON messages on: pinged every 30 s, reconnected with backoff (after
 * renewing the session token with any API call), not while the tab is hidden, and never after a
 * TASK_LIVE_FORBIDDEN close. `onMessage` gets null after a reconnect (messages may have been missed).
 * Used by openProjectLive and Chat's session page (T-51). Answers the function that closes it.
 */
export function openLiveSocket<T>(
  urlFor: (token: string) => string | Promise<string | null>,
  onMessage: (message: T | null) => void
): () => void {
  let socket: WebSocket | null = null;
  let ping: ReturnType<typeof setInterval> | undefined;
  let retry: ReturnType<typeof setTimeout> | undefined;
  let attempts = 0;
  let stopped = false;
  let waitingForVisible = false;

  const connect = async () => {
    // Opened before the page-load session check answered (T-68): wait for the token.
    if ((await whenSessionReady()) !== 'send' || stopped) return;
    const token = useAuthStore.getState().user?.token;
    if (!token || stopped) return;
    // A URL can need a request first (Chat's one-minute ticket); without one, try again later.
    const url = await Promise.resolve(urlFor(token)).catch(() => null);
    if (stopped) return;
    if (!url) {
      attempts += 1;
      retry = setTimeout(reconnect, Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(attempts, 5)));
      return;
    }
    socket = new WebSocket(url);
    socket.onopen = () => {
      if (attempts > 0) onMessage(null);
      attempts = 0;
      ping = setInterval(() => socket?.readyState === WebSocket.OPEN && socket.send('ping'), PING_MS);
    };
    socket.onmessage = (event) => {
      if (event.data === 'pong') return;
      let message: T;
      try {
        message = JSON.parse(String(event.data)) as T;
      } catch {
        return; // Not one of ours.
      }
      onMessage(message);
    };
    socket.onclose = (event) => {
      clearInterval(ping);
      if (stopped || event.code === TASK_LIVE_FORBIDDEN) return;
      attempts += 1;
      retry = setTimeout(reconnect, Math.min(MAX_BACKOFF_MS, 1000 * 2 ** Math.min(attempts, 5)));
    };
  };
  // An expired token closes the socket; any API call renews it before the retry.
  const reconnect = () => {
    if (stopped) return;
    if (document.hidden) {
      waitingForVisible = true;
      return;
    }
    void getTaskNotifications().finally(() => void connect());
  };
  const onVisibility = () => {
    if (document.hidden || !waitingForVisible) return;
    waitingForVisible = false;
    clearTimeout(retry);
    reconnect();
  };

  document.addEventListener('visibilitychange', onVisibility);
  void connect();
  return () => {
    stopped = true;
    document.removeEventListener('visibilitychange', onVisibility);
    clearInterval(ping);
    clearTimeout(retry);
    socket?.close();
  };
}
